import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp,
    ToggleLeft, ToggleRight, Tag, AlertTriangle, Upload, ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Image Upload Helper ──────────────────────────────────────────────────────

function ImageUploadField({ value, onChange, label, hint }) {
    const ref = useRef();
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChange(data.url);
            toast.success('Imagen subida');
        } catch { toast.error('Error al subir imagen'); }
        finally { setUploading(false); }
    };

    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
            <div className="flex items-center gap-2 mt-1">
                <div className="h-12 w-12 rounded-xl border bg-secondary/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {value
                        ? <img src={value} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                        : <ImageIcon className="h-5 w-5 text-muted-foreground" />
                    }
                </div>
                <div className="flex-1 space-y-1">
                    <input type="file" ref={ref} accept="image/*" className="hidden" onChange={pick} />
                    <Button type="button" variant="outline" size="sm" className="w-full text-xs h-8" onClick={() => ref.current?.click()} disabled={uploading}>
                        <Upload className="h-3 w-3 mr-1" />{uploading ? 'Subiendo…' : 'Subir imagen'}
                    </Button>
                    {value && <Input value={value} onChange={e => onChange(e.target.value)} className="h-7 text-[10px] font-mono" placeholder="https://..." />}
                </div>
            </div>
            {value && (
                <button type="button" onClick={() => onChange('')} className="text-[10px] text-red-500 hover:underline mt-0.5">Quitar imagen</button>
            )}
            {!value && (
                <Input value="" onChange={e => onChange(e.target.value)} placeholder="O pega una URL directamente…" className="mt-1 text-xs h-8" />
            )}
        </div>
    );
}

const EMPTY_FORM = {
    slug: '', name: '', tagline: '', description: '',
    icon: '🏷️', icon_image_url: '', color: '#dc2626', gradient_from: '#dc2626', gradient_to: '#991b1b',
    banner_url: '', banner_image_url: '', home_url: '', business_types: '', order: 99, active: true,
};

function SuperCatForm({ initial = EMPTY_FORM, onSave, onCancel, loading }) {
    const [form, setForm] = useState({ ...initial, business_types: Array.isArray(initial.business_types) ? initial.business_types.join(', ') : initial.business_types });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = e => {
        e.preventDefault();
        const payload = {
            ...form,
            business_types: form.business_types ? form.business_types.split(',').map(s => s.trim()).filter(Boolean) : [],
            order: parseInt(form.order) || 99,
        };
        onSave(payload);
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs">Slug <span className="text-red-500">*</span></Label>
                    <Input value={form.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                        placeholder="okfood" required className="mt-1" disabled={!!initial.id} />
                    {!initial.id && <p className="text-[10px] text-muted-foreground mt-0.5">Solo letras minúsculas, números y guiones. No se puede cambiar después.</p>}
                </div>
                <div>
                    <Label className="text-xs">Nombre <span className="text-red-500">*</span></Label>
                    <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="OkFood" required className="mt-1" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs">Tagline</Label>
                    <Input value={form.tagline} onChange={e => set('tagline', e.target.value)} placeholder="Consume local, pide en línea" className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs">Ícono (emoji)</Label>
                    <Input value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="🍔" className="mt-1 text-xl" maxLength={4} />
                </div>
            </div>
            <div>
                <Label className="text-xs">Descripción</Label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                    rows={2} placeholder="Descripción breve de la super categoría"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <Label className="text-xs">Color principal</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={form.color} onChange={e => set('color', e.target.value)} className="h-8 w-10 rounded cursor-pointer border" />
                        <Input value={form.color} onChange={e => set('color', e.target.value)} className="flex-1 h-8 text-xs font-mono" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Gradiente desde</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={form.gradient_from} onChange={e => set('gradient_from', e.target.value)} className="h-8 w-10 rounded cursor-pointer border" />
                        <Input value={form.gradient_from} onChange={e => set('gradient_from', e.target.value)} className="flex-1 h-8 text-xs font-mono" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Gradiente hasta</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={form.gradient_to} onChange={e => set('gradient_to', e.target.value)} className="h-8 w-10 rounded cursor-pointer border" />
                        <Input value={form.gradient_to} onChange={e => set('gradient_to', e.target.value)} className="flex-1 h-8 text-xs font-mono" />
                    </div>
                </div>
            </div>
            {/* Image uploads */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border rounded-xl p-4 bg-secondary/20">
                <ImageUploadField
                    value={form.banner_image_url || form.banner_url || ''}
                    onChange={v => set('banner_image_url', v)}
                    label="Imagen de banner / portada"
                    hint="Se muestra en la carta de la super-categoría en el home (ratio 4:5, como AdCard)"
                />
                <ImageUploadField
                    value={form.icon_image_url || ''}
                    onChange={v => set('icon_image_url', v)}
                    label="Imagen de ícono"
                    hint="Se muestra en el tile de categoría (cuadrado 80×80). PNG/SVG transparente recomendado."
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs">URL de inicio</Label>
                    <Input value={form.home_url} onChange={e => set('home_url', e.target.value)} placeholder="/okgo?cat=slug" className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs">URL de banner (texto, alternativa)</Label>
                    <Input value={form.banner_url} onChange={e => set('banner_url', e.target.value)} placeholder="https://..." className="mt-1" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs">Tipos de negocio (separados por coma)</Label>
                    <Input value={form.business_types} onChange={e => set('business_types', e.target.value)}
                        placeholder="fast_food, pizzeria, tacos" className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs">Orden</Label>
                    <Input type="number" value={form.order} onChange={e => set('order', e.target.value)} min={0} className="mt-1" />
                </div>
            </div>
            <div className="flex items-center gap-3">
                <button type="button" onClick={() => set('active', !form.active)}
                    className={`flex items-center gap-2 text-sm ${form.active ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {form.active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    {form.active ? 'Activa' : 'Inactiva'}
                </button>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={onCancel} disabled={loading}><X className="h-4 w-4 mr-1" />Cancelar</Button>
                <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-1" />{loading ? 'Guardando…' : 'Guardar'}
                </Button>
            </div>
        </form>
    );
}

export default function AdminSuperCategorias() {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // slug of cat being edited
    const [deleting, setDeleting] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/super-categories');
            setCats(data || []);
        } catch { toast.error('Error al cargar super categorías'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (payload) => {
        setSaving(true);
        try {
            await api.post('/admin/super-categories', payload);
            toast.success('Super categoría creada');
            setCreating(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear'); }
        finally { setSaving(false); }
    };

    const handleUpdate = async (slug, payload) => {
        setSaving(true);
        try {
            await api.put(`/admin/super-categories/${slug}`, payload);
            toast.success('Super categoría actualizada');
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
        finally { setSaving(false); }
    };

    const handleToggleActive = async (cat) => {
        try {
            await api.put(`/admin/super-categories/${cat.slug}`, { active: !cat.active });
            toast.success(cat.active ? 'Desactivada' : 'Activada');
            load();
        } catch (e) { toast.error('Error al cambiar estado'); }
    };

    const handleDelete = async (slug) => {
        setDeleting(slug);
        try {
            await api.delete(`/admin/super-categories/${slug}`);
            toast.success('Super categoría eliminada');
            setDeleting(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al eliminar'); setDeleting(null); }
    };

    const toggleExpand = (slug) => setExpanded(prev => ({ ...prev, [slug]: !prev[slug] }));

    return (
        <div className="space-y-6 max-w-4xl">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Super Categorías</h1>
                    <p className="text-sm text-muted-foreground">Crea y gestiona las super categorías de la plataforma OkGo.</p>
                </div>
                {!creating && (
                    <Button onClick={() => { setCreating(true); setEditing(null); }} className="bg-red-600 hover:bg-red-700">
                        <Plus className="h-4 w-4 mr-2" />Nueva super categoría
                    </Button>
                )}
            </div>

            {/* Create form */}
            {creating && (
                <div className="rounded-xl border bg-card p-5">
                    <h2 className="font-medium mb-4 flex items-center gap-2"><Plus className="h-4 w-4 text-red-600" />Nueva super categoría</h2>
                    <SuperCatForm onSave={handleCreate} onCancel={() => setCreating(false)} loading={saving} />
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
            ) : cats.length === 0 ? (
                <div className="rounded-xl border bg-card p-8 text-center">
                    <Tag className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No hay super categorías aún.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {cats.map(cat => (
                        <div key={cat.slug} className="rounded-xl border bg-card overflow-hidden">
                            {/* Header row */}
                            <div className="flex items-center gap-3 p-4">
                                {/* Color swatch */}
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-xl shrink-0"
                                    style={{ background: `linear-gradient(135deg, ${cat.gradient_from}, ${cat.gradient_to})` }}>
                                    {cat.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{cat.name}</span>
                                        <span className="text-xs text-muted-foreground font-mono">{cat.slug}</span>
                                        {cat.is_demo && <Badge variant="outline" className="text-[10px] py-0">demo</Badge>}
                                        <Badge variant={cat.active ? 'default' : 'secondary'}
                                            className={`text-[10px] py-0 ${cat.active ? 'bg-green-100 text-green-700 border-green-300' : ''}`}>
                                            {cat.active ? 'Activa' : 'Inactiva'}
                                        </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate">{cat.tagline}</p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => handleToggleActive(cat)} title={cat.active ? 'Desactivar' : 'Activar'}
                                        className={`p-1.5 rounded hover:bg-secondary ${cat.active ? 'text-green-600' : 'text-muted-foreground'}`}>
                                        {cat.active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                    </button>
                                    <button onClick={() => { setEditing(editing === cat.slug ? null : cat.slug); setCreating(false); }}
                                        className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground">
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => toggleExpand(cat.slug)}
                                        className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground">
                                        {expanded[cat.slug] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Expanded details */}
                            {expanded[cat.slug] && editing !== cat.slug && (
                                <div className="px-4 pb-4 border-t pt-3 space-y-2 text-xs text-muted-foreground">
                                    {cat.description && <p>{cat.description}</p>}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        <div><span className="font-medium text-foreground">URL inicio:</span> {cat.home_url || '—'}</div>
                                        <div><span className="font-medium text-foreground">Orden:</span> {cat.order}</div>
                                        <div><span className="font-medium text-foreground">Tipos:</span> {(cat.business_types || []).join(', ') || '—'}</div>
                                    </div>
                                    {cat.banner_url && (
                                        <div><span className="font-medium text-foreground">Banner:</span> <a href={cat.banner_url} target="_blank" rel="noreferrer" className="underline text-blue-600 break-all">{cat.banner_url}</a></div>
                                    )}
                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={() => { if (window.confirm(`¿Eliminar "${cat.name}"? Esta acción no se puede deshacer.`)) handleDelete(cat.slug); }}
                                            disabled={deleting === cat.slug}
                                            className="flex items-center gap-1 text-red-600 hover:text-red-700 text-xs">
                                            <Trash2 className="h-3.5 w-3.5" />{deleting === cat.slug ? 'Eliminando…' : 'Eliminar'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Edit form */}
                            {editing === cat.slug && (
                                <div className="px-4 pb-4 border-t pt-4">
                                    <SuperCatForm
                                        initial={cat}
                                        onSave={(payload) => handleUpdate(cat.slug, payload)}
                                        onCancel={() => setEditing(null)}
                                        loading={saving}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex gap-3 text-xs text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                <div>
                    <span className="font-medium">Aviso: </span>Eliminar una super categoría que tiene negocios activos puede causar inconsistencias.
                    Se recomienda desactivarla en lugar de eliminarla. Los negocios asociados no se eliminarán automáticamente.
                </div>
            </div>
        </div>
    );
}
